import axios from "axios";
import Cookies from "js-cookie";
import { handleInvalidToken } from "../utils/tokenUtils";

/**
 * Lazily-injected Redux store reference.
 * Avoids the circular dependency:
 *   store.ts → rootReducer → actions → instance.js → store.ts
 *
 * Call setStore(store) in index.js immediately after the store is created,
 * before ReactDOM.render. The interceptors only execute at request time
 * (not at module evaluation time), so the reference will always be set by then.
 */
let _store = null;
export const setStore = (s) => {
  _store = s;
};

// const localURL = `http://localhost:3000`;
const liveBaseURL = import.meta.env.VITE_BACKEND_APP_URL;
const websocketUrl = import.meta.env.VITE_WEBSOCKET_URL;

export const baseURL = `${liveBaseURL}/`;

const instance = axios.create({
  baseURL,
});

// Track CSRF token refresh to prevent infinite loops
let isRefreshingCSRF = false;
let failedQueue = [];

/** Single global gate for session-auth 401 handling (see handleInvalidToken / tokenUtils). */
let sessionAuthInvalidationDispatched = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Function to clear CSRF tokens (both sessionStorage and cookies)
export const clearCSRFCookies = () => {
  // Clear from sessionStorage (tab-specific)
  sessionStorage.removeItem("csrf_token");
  sessionStorage.removeItem("csrftoken");

  // Clear cookies using js-cookie (for backward compatibility)
  Cookies.remove("csrftoken", { path: "/" });
  Cookies.remove("csrf_token", { path: "/" });

  // Also clear from document.cookie as fallback
  document.cookie = "csrftoken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie =
    "csrf_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";

  // Clear for all possible domains
  const hostname = window.location.hostname;
  const domainParts = hostname.split(".");
  if (domainParts.length > 1) {
    // Try clearing for parent domain
    const parentDomain = "." + domainParts.slice(-2).join(".");
    Cookies.remove("csrftoken", { path: "/", domain: parentDomain });
    Cookies.remove("csrf_token", { path: "/", domain: parentDomain });
  }
};

// Function to get CSRF token from sessionStorage (tab-specific)
const getCSRFTokenFromStorage = () => {
  return (
    sessionStorage.getItem("csrf_token") || sessionStorage.getItem("csrftoken")
  );
};

// Function to set CSRF token in sessionStorage (tab-specific)
const setCSRFTokenInStorage = (token) => {
  sessionStorage.setItem("csrf_token", token);
  sessionStorage.setItem("csrftoken", token); // Store both names for compatibility
};

// Function to refresh CSRF token
// Only called when we actually get a CSRF error (not proactively)
// Uses a separate axios instance to avoid interceptors and infinite loops
// When called due to CSRF error, clears old tokens first to ensure fresh token
const refreshCSRFToken = async (clearOldTokens = false) => {
  try {
    // If clearOldTokens is true (CSRF error detected), clear all old tokens first
    // This ensures we get a completely fresh token from the backend
    if (clearOldTokens) {
      clearCSRFCookies();
    }

    // Only make API call to fetch a fresh CSRF token
    // Create a separate axios instance without interceptors to avoid circular dependencies
    const refreshInstance = axios.create({
      baseURL,
      withCredentials: true,
    });

    // Make a simple GET request to fetch a new CSRF token
    // The backend will set a new CSRF token cookie in the response
    // Using a lightweight endpoint like root or a health check endpoint
    const response = await refreshInstance.get("", {
      withCredentials: true,
    });

    // Try to get token from response headers first (most reliable)
    const tokenFromHeader =
      response.headers["x-csrftoken"] ||
      response.headers["csrf-token"] ||
      response.headers["X-CSRF-TOKEN"];
    if (tokenFromHeader) {
      // Store in sessionStorage (tab-specific) instead of cookies
      setCSRFTokenInStorage(tokenFromHeader);
      return tokenFromHeader;
    }

    // Fallback: Extract CSRF token from cookies (backend might set it)
    // Wait a bit for cookie to be set (sometimes there's a delay)
    await new Promise((resolve) => setTimeout(resolve, 100));
    const newCSRFToken = Cookies.get("csrftoken") || Cookies.get("csrf_token");

    if (newCSRFToken) {
      // Store in sessionStorage (tab-specific) for future use
      setCSRFTokenInStorage(newCSRFToken);
      return newCSRFToken;
    }

    // If still no token, try reading from document.cookie as fallback
    const allCookies = document.cookie.split(";");
    for (let cookie of allCookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "csrftoken" || name === "csrf_token") {
        // Store in sessionStorage (tab-specific)
        setCSRFTokenInStorage(value);
        return value;
      }
    }

    throw new Error("CSRF token not found in response");
  } catch (refreshError) {
    console.error("Failed to refresh CSRF token:", refreshError);
    throw refreshError;
  }
};

/** Auth token for API: prefer live Redux (sync right after login), then redux-persist blob (async write). */
function getAuthTokenFromClient() {
  try {
    const fromStore = _store?.getState?.()?.auth?.token;
    if (fromStore) return fromStore;
  } catch (e) {
    // ignore
  }
  if (typeof window === "undefined") return null;
  const persistedState = sessionStorage.getItem("persist:root");
  if (!persistedState) return null;
  try {
    const parsedState = JSON.parse(persistedState);
    const authState = JSON.parse(parsedState.auth || "{}");
    return authState.token || null;
  } catch (error) {
    console.error("Error parsing auth state:", error);
    return null;
  }
}

/**
 * CSRF token for a request sent OUTSIDE this axios instance (raw XHR/fetch).
 *
 * Same lookup order as the request interceptor below — sessionStorage first
 * (tab-isolated sessions), cookies as the legacy fallback — so a raw caller
 * cannot drift from the interceptor's notion of "the token".
 */
export const getCSRFTokenForRawRequest = () => {
  try {
    return (
      getCSRFTokenFromStorage() ||
      Cookies.get("csrftoken") ||
      Cookies.get("csrf_token") ||
      null
    );
  } catch {
    // sessionStorage can throw in privacy modes.
    return null;
  }
};

/**
 * Headers for a telemetry request sent OUTSIDE this axios instance.
 *
 * `fetch(..., { keepalive: true })` is the only transport that survives page
 * unload while still carrying headers, and it bypasses every interceptor below —
 * so the auth and CSRF headers the request interceptor would have added have to
 * be assembled by hand. Exported rather than reimplemented at the call site
 * because the CSRF key names live in exactly one place (getCSRFTokenFromStorage
 * above); a second copy of `"csrf_token" || "csrftoken"` in another module is how
 * those two drift apart.
 *
 * Missing Authorization here is not a soft failure: it is a 401 on the unload
 * path only, which passes every manual test and loses the terminating event.
 */
export const getTelemetryHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token = getAuthTokenFromClient();
  if (token) headers.Authorization = `Token ${token}`;
  // DRF TokenAuthentication is CSRF-exempt, so this is belt-and-braces — but the
  // contract specifies POSTs carry it, and sending it costs nothing.
  try {
    const csrf = getCSRFTokenFromStorage();
    if (csrf) headers["X-CSRF-TOKEN"] = csrf;
  } catch {
    // sessionStorage can throw in privacy modes — send what we have.
  }
  return headers;
};

// Request interceptor to add auth token and CSRF token
instance.interceptors.request.use(
  async (config) => {
    const token = getAuthTokenFromClient();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Token ${token}`;
    }

    // Add CSRF token for state-changing requests
    // Use sessionStorage (tab-specific) instead of cookies to match tab-isolated sessions
    if (
      ["post", "put", "patch", "delete"].includes(config.method?.toLowerCase())
    ) {
      // First try to get from sessionStorage (tab-specific)
      let csrfToken = getCSRFTokenFromStorage();

      // Fallback to cookies for backward compatibility (but prefer sessionStorage)
      if (!csrfToken) {
        csrfToken = Cookies.get("csrftoken") || Cookies.get("csrf_token");
        // If found in cookies, migrate to sessionStorage for tab isolation
        if (csrfToken) {
          setCSRFTokenInStorage(csrfToken);
        }
      }

      // Only add token if we have it - don't fetch proactively
      // If token is missing, let the request proceed and handle CSRF error in response interceptor
      // This avoids unnecessary API calls
      if (csrfToken && !config.headers["X-CSRF-TOKEN"]) {
        config.headers["X-CSRF-TOKEN"] = csrfToken;
      }
    }

    // Mark that this request should not retry if it's already a retry
    if (!config._retry) {
      config._retry = false;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle errors
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle CSRF token errors (403 Forbidden or 400 Bad Request)
    // Some backends return 400 for CSRF errors instead of 403
    if (
      error.response &&
      (error.response.status === 403 || error.response.status === 400) &&
      ["post", "put", "patch", "delete"].includes(
        originalRequest?.method?.toLowerCase(),
      )
    ) {
      const errorMessage =
        error.response.data?.detail ||
        error.response.data?.message ||
        error.response.data?.error ||
        "";

      // Check if it's a CSRF-related error
      // Handle various CSRF error messages: missing, invalid, expired, failed, etc.
      const errorMessageLower = errorMessage.toLowerCase();
      // Skip CSRF retry for endpoints that return business-logic token errors (e.g. validate-link)
      const requestUrl = (originalRequest?.url || "").toLowerCase();
      const isValidateLinkRequest = requestUrl.includes("validate-link");
      const isCSRFError =
        !isValidateLinkRequest &&
        (errorMessageLower.includes("csrf") ||
          errorMessageLower.includes("forbidden") ||
          (errorMessageLower.includes("token") &&
            (errorMessageLower.includes("missing") ||
              errorMessageLower.includes("invalid") ||
              errorMessageLower.includes("expired") ||
              errorMessageLower.includes("failed"))) ||
          errorMessageLower.includes("csrf failed") ||
          errorMessageLower.includes("csrf token missing") ||
          errorMessageLower.includes("csrf token invalid") ||
          (!errorMessage && error.response.status === 403)); // Sometimes CSRF errors don't have a message

      if (isCSRFError && !originalRequest._retry) {
        // Prevent multiple simultaneous refresh attempts
        if (isRefreshingCSRF) {
          // Queue this request to retry after token refresh
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["X-CSRF-TOKEN"] = token;
              return instance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshingCSRF = true;

        try {
          // When CSRF error is detected, clear old tokens first and fetch a fresh one
          // This handles stale/expired tokens that might be causing validation issues
          // Pass clearOldTokens=true to ensure we get a completely fresh token
          const newCSRFToken = await refreshCSRFToken(true);

          // Store in sessionStorage (tab-specific) - refreshCSRFToken already does this, but ensure it's set
          setCSRFTokenInStorage(newCSRFToken);

          // Update the original request with new token
          originalRequest.headers["X-CSRF-TOKEN"] = newCSRFToken;

          // Process queued requests
          processQueue(null, newCSRFToken);
          isRefreshingCSRF = false;

          // Retry the original request with the new CSRF token
          return instance(originalRequest);
        } catch (refreshError) {
          // If refresh fails, reject all queued requests
          processQueue(refreshError, null);
          isRefreshingCSRF = false;

          console.error("CSRF token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }
    }

    // Handle 401 errors with specific conditions
    if (error.response && error.response.status === 401) {
      const errorMessage =
        error.response.data?.detail ||
        error.response.data?.message ||
        error.response.data?.error ||
        "";
      const msg = String(errorMessage).toLowerCase();

      const hadAuthHeader = !!(
        error.config?.headers?.Authorization ||
        error.config?.headers?.authorization
      );

      // Narrow messages that indicate invalid/expired *session auth*, not every 401
      // (e.g. permission denied or business rules often return 401 with different text).
      const isLikelySessionAuthFailure =
        msg.includes("authentication credentials were not provided") ||
        msg.includes("credentials were not provided") ||
        msg.includes("token not valid") ||
        msg.includes("invalid token") ||
        msg.includes("given token not valid") ||
        (msg.includes("not valid") && msg.includes("token")) ||
        msg.includes("token has expired") ||
        msg.includes("session has expired") ||
        msg.includes("login session") ||
        (msg.includes("expired") &&
          (msg.includes("token") ||
            msg.includes("session") ||
            msg.includes("signature"))) ||
        (hadAuthHeader && msg.length === 0);

      // Token may exist in Redux before redux-persist finishes writing sessionStorage
      const hasToken = !!getAuthTokenFromClient();

      const authHeaderMissing = !hadAuthHeader;
      const isMissingCredentialsOnly =
        authHeaderMissing &&
        msg.includes("authentication credentials were not provided");

      const isAuthEndpoint =
        error.config?.url?.includes("/accounts/login") ||
        error.config?.url?.includes("/accounts/social-login") ||
        error.config?.url?.includes("/accounts/token/renew") ||
        error.config?.url?.includes("/accounts/logout");

      // Proctoring telemetry must NEVER be able to end a candidate's session.
      //
      // These fire repeatedly during a live interview, always with an
      // Authorization header. `accounts/middlewares/customtoken.py` returns
      // 401 {"error": "Token has expired"} on ANY endpoint, and that body matches
      // `isLikelySessionAuthFailure` above word-for-word — so one such 401 on a
      // tab-switch ping would call handleInvalidToken() and log the candidate out
      // mid-interview. The interview is unrecoverable at that point; a dropped
      // telemetry ping is not. Losing the ping is always the better trade.
      //
      // Matched by URL rather than a request flag so it cannot be forgotten at a
      // call site, and to mirror isAuthEndpoint's existing convention. ADD ANY NEW
      // TELEMETRY ROUTE HERE — `interviews/proctor-event` is listed ahead of its
      // client integration on purpose.
      const isProctoringTelemetry = [
        "increment-tab-switch",
        "increment-external-screen",
        "update-response-delayed-in-interview",
        "update-response-delayed-flag",
        "increment-skip-question-count",
        "proctor-event",
      ].some((route) => error.config?.url?.includes(route));

      if (isMissingCredentialsOnly) {
        console.warn(
          "401 with no Authorization header sent — not showing session expired:",
          error.config?.url,
        );
        return Promise.reject(error);
      }

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      if (isProctoringTelemetry) {
        console.warn(
          "401 on proctoring telemetry — rejecting to the caller, NOT invalidating the session:",
          error.config?.url,
        );
        return Promise.reject(error);
      }

      if (hasToken && isLikelySessionAuthFailure) {
        console.error("Session expired - Token validation failed:", {
          errorMessage,
          url: error.config?.url,
          method: error.config?.method,
          status: error.response.status,
        });
        if (!sessionAuthInvalidationDispatched) {
          sessionAuthInvalidationDispatched = true;
          handleInvalidToken();
        }
      } else if (!hasToken) {
        // no-op
      } else {
        console.warn(
          "401 with token but not treated as session auth failure:",
          {
            errorMessage,
            url: error.config?.url,
            method: error.config?.method,
          },
        );
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
export { websocketUrl };
